/**
 * What a product fills in when it is put on an estimate line — and what a line
 * that is not a product starts as.
 *
 * Two things came out of the same conversation (Jonathon, 2026-09-01):
 *
 *   "gutters and gutter guards need to be a product as well that we can select
 *    in the estimate builder with appropriate verbiage instead of just
 *    upgrades. there also needs to be a custom repair option ... where we can
 *    just enter in everything manually, select the trade etc."
 *
 * Both are the same gap seen from two ends. Picking a product filled in a
 * catalogue label and nothing else: no scope, no quantity, no price — so
 * selling gutters as a line meant typing the sentence, working the eave out on
 * a phone and typing the rate. And a repair, which is not in any catalogue, had
 * no obvious way in at all.
 *
 * ## A suggestion, never a rule
 *
 * Everything here lands in an editable box, and nothing overwrites something a
 * person has already typed. That is why every default is `null`-able rather
 * than falling back to a plausible number: a quantity of 1 nobody looked at
 * twice is worse than an empty box, and this is the file that decides which one
 * a rep gets.
 *
 * The money is worked out here rather than in the modal — `packages/core` owns
 * arithmetic on prices, and the rate × quantity a customer is quoted is exactly
 * the kind that must not live in a component.
 */

import { measuredQuantity } from "./measuredQuantity.js";
import { rateTimesQty, toDecimal, toUnitRate } from "./money.js";
import {
  estimateLineDescription,
  type ProductWording,
} from "./productWording.js";
import type { RoofLayouts } from "./generator.js";

/** As much of a product as a line needs to fill itself in. */
export interface LineProduct extends ProductWording {
  unit?: string | null | undefined;
  default_price?: number | string | null | undefined;
  /** The scope this product puts under the heading. See `customer_scope`. */
  customer_scope?: string | null | undefined;
  coverage?: number | string | null | undefined;
  waste?: number | string | null | undefined;
  measurement?: string | null | undefined;
}

/** What picking a product offers to write onto the line. */
export interface EstimateLineDefaults {
  /** The heading the customer reads. Always something. */
  description: string;
  /** The scope underneath it, when the product carries one. */
  note: string | null;
  unit: string | null;
  /** Worked out from the roof, or null when it cannot be. */
  qty: number | null;
  /** "Eave + 10% + downspouts = 198 LF" — why the quantity says what it says. */
  basis: string | null;
  /** The customer's rate, not the cost. Null when the product has no price. */
  unitPrice: number | null;
  /** Rate × quantity, when both are known. */
  customerPrice: number | null;
}

/**
 * The scope a product puts under the line's heading.
 *
 * Deliberately its own column and not `products.description`, for the same
 * reason `customer_wording` is: `description` is the blurb under a proposal
 * upgrade and holds whatever was imported on hundreds of rows. Whitespace is
 * treated as unset — a box somebody opened and left alone is not a scope.
 */
export function estimateLineScope(product: LineProduct): string | null {
  const scope = (product.customer_scope ?? "").trim();
  return scope === "" ? null : scope;
}

/**
 * What "Save this wording to <product>" would do, if anything (ADR 0104).
 *
 * - `null` — nothing to offer: the box is empty, or already says what the
 *   product says, and a button that is a no-op teaches a rep to ignore it.
 * - `ok: false` — refused, with the sentence that says why.
 * - `ok: true` — the scope to write, and the question to ask first.
 *
 * **A scope carrying protective clauses is refused.** Since ADR 0100 an
 * AI-drafted line keeps its clauses — leak may persist, colour may not match —
 * in `estimate_items.scope_qualifiers`, apart from the note. A product has no
 * such column: it carries the note and nothing else. Copying only the note
 * would send every future line out without the protection this one has, and
 * nobody would see it go. Until a product can hold clauses, the wording stays
 * on the line.
 *
 * **It always asks, and names the product.** One click rewrites what every
 * future line of that product starts from, and the previous wording is gone.
 */
export type ScopeTeaching =
  { ok: true; scope: string; confirm: string } | { ok: false; reason: string };

export function scopeTeaching(input: {
  productName: string;
  /** The product's `customer_scope` as it stands. */
  productScope: string | null | undefined;
  /** The line's note, as typed. */
  note: string | null | undefined;
  /** The line's `scope_qualifiers`. */
  qualifiers?: readonly string[] | null;
}): ScopeTeaching | null {
  const scope = (input.note ?? "").trim();
  const saved = estimateLineScope({ customer_scope: input.productScope });
  if (scope === "" || scope === saved) return null;

  const clauses = (input.qualifiers ?? []).filter((id) => id.trim() !== "");
  if (clauses.length > 0)
    return {
      ok: false,
      reason:
        "This scope carries protective clauses, and a product has nowhere to " +
        "keep them — saving the wording would send every future line out " +
        "without them. The clauses stay on this line.",
    };

  return {
    ok: true,
    scope,
    confirm: saved
      ? `Replace the scope of work on ${input.productName}? Every future line ` +
        `of it starts from this wording, and what it says now is gone. Lines ` +
        `already on estimates keep theirs.`
      : `Save this as the scope of work on ${input.productName}? Every future ` +
        `line of it starts from this wording.`,
  };
}

/**
 * Everything a product offers to fill in, given the roof it is going on.
 *
 * The quantity comes from the product's own measurement key — gutters know they
 * are eave plus 10% plus downspouts — which is the same arithmetic a work order
 * line already uses. A product with no key, or a job with no roof measured,
 * simply returns null for the quantity, and the box stays as it was.
 */
export function estimateLineDefaults(
  product: LineProduct,
  measurements?: RoofLayouts | null,
): EstimateLineDefaults {
  const measured = measuredQuantity(
    {
      coverage: product.coverage,
      waste: product.waste,
      measurement: product.measurement,
    },
    measurements,
  );
  const rate = toUnitRate(product.default_price);
  const unitPrice = rate > 0 ? rate : null;
  const qty = measured?.qty ?? null;

  return {
    description: estimateLineDescription(product),
    note: estimateLineScope(product),
    unit: product.unit ?? null,
    qty,
    basis: measured?.basis ?? null,
    unitPrice,
    customerPrice:
      unitPrice != null && qty != null
        ? toDecimal(rateTimesQty(rate, qty))
        : null,
  };
}

/**
 * The one-off repair a rep types in themselves.
 *
 * Not a catalogue product and never going to be: a chimney flashing nobody has
 * measured, a soffit panel, half a day putting a gable end back. It is offered
 * at the top of the product picker rather than hidden behind an empty box,
 * because "leave the product blank" is not something anybody guesses.
 *
 * Sold at a number, so the unit is each and the quantity is one — the rep types
 * the price the repair is worth and nothing multiplies it out behind them. The
 * trade is deliberately left alone: a repair is as often siding or gutters as
 * it is roofing, and the line already carries the estimate's trade, which is a
 * better guess than any this could make.
 */
export const CUSTOM_REPAIR_UNIT = "EA";
export const CUSTOM_REPAIR_QTY = 1;

/**
 * What choosing "Custom Repair" writes onto the line.
 *
 * The rate goes with the quantity. A line that was 12 LF at $100 carries
 * `unit_price = 100` and `customer_price = 1200`; making it one of a thing
 * and leaving the rate behind would store a row where qty x unit_price is
 * not the price, which is the one invariant the editor keeps. The price
 * itself stays: a repair is sold at a number, and $1,200 may well still be
 * the number.
 */
export function customRepairLine(): {
  product_id: null;
  unit: string;
  qty: number;
  unit_price: null;
  color: null;
} {
  return {
    product_id: null,
    unit: CUSTOM_REPAIR_UNIT,
    qty: CUSTOM_REPAIR_QTY,
    unit_price: null,
    color: null,
  };
}

/**
 * Whether a product may be offered on an estimate line at all.
 *
 * Jonathon, 2026-09-04: "i only want products available to select on the
 * estimate line item section if they have notes (scope of work) ... all of the
 * rest can show up where appropriate on material order, work order, and other
 * expenses but the main point on the estimate line item is to provide a
 * customer a scope of work."
 *
 * The catalogue is 634 products and all but a few dozen are parts — nails,
 * sealant, drip edge, a single metal panel. A crew and a supplier need every
 * one of them; a homeowner reading a proposal needs none. So the estimate line
 * offers what carries a scope, and everywhere else — material orders, work
 * orders, returns, other expenses — keeps the whole catalogue. There is no
 * second flag to keep in step: writing the scope is what puts a product in
 * front of a customer, and clearing it takes it away again.
 *
 * A line already carrying a product is the exception, in `estimateProducts`
 * below: the rule governs what may be *chosen*, and must never blank out a row
 * somebody sold last month.
 */
export function sellsOnEstimate(product: LineProduct): boolean {
  return estimateLineScope(product) !== null;
}

/**
 * The catalogue as an estimate line offers it: what has a scope, plus whatever
 * this line already has.
 *
 * The second part matters more than it looks. `ProductSelect` shows the name of
 * the selected product by finding it in the list it was given, so a product
 * dropped from the list is a row that reads as empty — and an old line whose
 * product has since lost its scope would look unsaved. Keeping it in means the
 * row still says what it says; it just cannot be chosen again from scratch.
 */
export function estimateProducts<T extends LineProduct & { id: string }>(
  products: readonly T[],
  selectedId?: string | null,
): T[] {
  return products.filter((p) => sellsOnEstimate(p) || p.id === selectedId);
}
